import { useParams } from "react-router-dom";
import { Field, Form } from "react-final-form";
import type { FormApi } from "final-form";
import {
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import {
  useCreateSchoolPupilSubscriptionMutation,
  useGetAdminPanelSubscriptionsPaymentTypeQuery,
  useGetSchoolPayersQuery,
  useGetSchoolPupilSubscriptionPeriodQuery,
  useGetSchoolPupilSubscriptionPlansQuery,
  useGetSchoolPupilSubscriptionQuery,
} from "../../../generated/graphql";
import { SubscriptionFormValues } from "../SchoolSubscription/SubscriptionFormsForSchools/utils";
import { ButtonCustom, ModalInput } from "../../shared/Style/Style";
import {
  commonPaymentTypeMap,
  subscriptionPlansMonths,
} from "../SchoolSubscription/maps";
import { costText } from "../constants";
import { useState, useRef } from "react";
import { Alert } from "@material-ui/lab";
import { SnackbarInterface } from "./SchoolPupilsSubscriptions";

const formInitialValues: SubscriptionFormValues = {
  paymentDate: "",
  validity: "",
  paymentType: "",
  planId: "",
  total: "",
};

export const CreateSchoolPupilsSubscription = () => {
  const [snackbar, setSnackbar] = useState<SnackbarInterface>({
    open: false,
    message: "",
    severity: "success",
  });
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<FormApi<SubscriptionFormValues> | null>(null);
  const { schoolId = "" } = useParams<{ schoolId: string }>();

  const { data: subscriptionPlansData } =
    useGetSchoolPupilSubscriptionPlansQuery();
  const subscriptionPlans =
    subscriptionPlansData?.getSchoolPupilSubscriptionPlans ?? [];

  const { data: paymentTypeData } =
    useGetAdminPanelSubscriptionsPaymentTypeQuery();
  const paymentTypes =
    paymentTypeData?.getAdminPanelSubscriptionsPaymentType ?? [];

  const { data: schoolPayerData } = useGetSchoolPayersQuery({
    fetchPolicy: "cache-and-network",
  });
  const schoolPayer = schoolPayerData?.getSchoolPayers ?? [];

  const { data: schoolPupilSubscriptionPeriodData } =
    useGetSchoolPupilSubscriptionPeriodQuery();
  const schoolPupilSubscriptionPeriods =
    schoolPupilSubscriptionPeriodData?.getSchoolPupilSubscriptionPeriod ?? [];

  const { refetch: refetchSchoolPupilsSubscriptionsData } =
    useGetSchoolPupilSubscriptionQuery({
      variables: { schoolId },
    });

  const [createSubscription] = useCreateSchoolPupilSubscriptionMutation({
    onCompleted: () => {
      refetchSchoolPupilsSubscriptionsData();
      setSnackbar({
        open: true,
        message: "Підписку успішно створено!",
        severity: "success",
      });
      setTimeout(() => {
        const currentForm = formRef.current;

        if (currentForm) {
          currentForm.batch(() => {
            currentForm.reset();
            const allFields = currentForm.getRegisteredFields();
            allFields.forEach((field) => {
              currentForm.resetFieldState(
                field as keyof SubscriptionFormValues
              );
            });
          });
        }
      }, 0);
    },
    onError: (err) => {
      console.error("Помилка створення підписки:", err);
      setSnackbar({
        open: true,
        message: "Не вдалося створити підписку!",
        severity: "error",
      });
    },
  });

  const validateForm = (values: SubscriptionFormValues) => {
    const errors: { [key: string]: string } = {};

    const isInvalidNumber = (value: any, min = 0) =>
      isNaN(Number(value)) || Number(value) < min;

    [
      "paymentDate",
      "planId",
      "paymentTypeId",
      "payerId",
      "total",
      "validity",
    ].forEach((field) => {
      if (!values[field]) {
        errors[field] = "Це поле обов'язкове";
      }
    });

    if (values.total?.length && isInvalidNumber(values.total)) {
      errors.total = "Значення має бути числом і не менше 0";
    }

    return errors;
  };

  const onFormSubmit = async (values: SubscriptionFormValues) => {
    try {
      setIsLoading(true);
      const { paymentDate, validity, paymentTypeId, planId, total, payerId } =
        values;

      await createSubscription({
        variables: {
          data: {
            schoolId,
            planId,
            schoolPupilSubscriptionPeriodId: validity,
            schoolPayerId: payerId,
            paymentDate: new Date(paymentDate),
            paymentTypeId,
            price: Number(total),
          },
        },
      });
    } catch {
      console.error("error createSubscription");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Form
        initialValues={formInitialValues}
        validate={validateForm}
        onSubmit={onFormSubmit}
      >
        {({ handleSubmit, form }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <Grid container justifyContent="flex-start" spacing={2}>
                <Grid item xs={6}>
                  <Field name="paymentDate">
                    {({ input, meta }) => (
                      <DatePicker
                        error={meta.error && meta.touched}
                        helperText={meta.touched && meta.error}
                        fullWidth
                        variant="inline"
                        format="dd.MM.yyyy"
                        label="Дата платежу"
                        inputVariant="filled"
                        value={input.value || null}
                        onChange={input.onChange}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name="validity">
                    {({ input, meta }) => (
                      <FormControl
                        variant="filled"
                        error={meta.error && meta.touched}
                        fullWidth
                      >
                        <InputLabel>Кількість місяців</InputLabel>
                        <Select
                          value={input.value}
                          onChange={(e) => input.onChange(e.target.value)}
                        >
                          {schoolPupilSubscriptionPeriods.map(
                            ({ id, displayName }) => (
                              <MenuItem key={id} value={id}>
                                {displayName}
                              </MenuItem>
                            )
                          )}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name="planId">
                    {({ input, meta }) => (
                      <FormControl
                        variant="filled"
                        error={meta.error && meta.touched}
                        fullWidth
                      >
                        <InputLabel>Тариф</InputLabel>
                        <Select
                          value={input.value}
                          onChange={(e) => input.onChange(e.target.value)}
                        >
                          {subscriptionPlans.map(
                            ({ id, name, description }) => (
                              <MenuItem key={id} value={id}>
                                {`${description ?? name} ${
                                  subscriptionPlansMonths[name]?.label || ""
                                }`}
                              </MenuItem>
                            )
                          )}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name="paymentTypeId">
                    {({ input, meta }) => (
                      <FormControl
                        variant="filled"
                        error={meta.error && meta.touched}
                        fullWidth
                      >
                        <InputLabel>Тип платежу</InputLabel>
                        <Select
                          value={input.value}
                          onChange={(e) => input.onChange(e.target.value)}
                        >
                          {paymentTypes.map(({ id, name }) => (
                            <MenuItem key={id} value={id}>
                              {commonPaymentTypeMap[name]}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name="total">
                    {({ input, meta }) => (
                      <ModalInput
                        label={costText}
                        error={meta.error && meta.touched}
                        helperText={meta.touched && meta.error}
                        variant="filled"
                        type="number"
                        {...input}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field name="payerId">
                    {({ input, meta }) => (
                      <FormControl
                        variant="filled"
                        error={meta.error && meta.touched}
                        fullWidth
                      >
                        <InputLabel>Платник</InputLabel>
                        <Select
                          value={input.value}
                          onChange={(e) => input.onChange(e.target.value)}
                        >
                          {schoolPayer.map(({ id, name }) => (
                            <MenuItem key={id} value={id}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <ButtonCustom disabled={isLoading} type="submit" width="200">
                    Оформити підписку
                  </ButtonCustom>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Form>
    </>
  );
};
